import * as React from "react";
import { motion, AnimatePresence } from "motion/react";
import { BookOpen, FileText, FolderOpen, Lightbulb, Search, X } from "lucide-react";
import { ActionInput } from "./ActionInput";
import { Button } from "../primitives/Button";
import { MicButton } from "./MicButton";
import { AikoMark } from "./AikoMark";
import { Skeleton } from "../primitives/Skeleton";
import { Spinner } from "../primitives/Spinner";
import { cn } from "../lib/cn";
import { useReducedMotion } from "../hooks/useReducedMotion";

/* ============================================================================
 * Types
 * ========================================================================= */

/**
 * Shape of a single item passed into `<SmartSearch data={...}>`. The built-in
 * scorer reads `name` (required), `description`, `type`, and `tags`. Any extra
 * keys flow through untouched — useful for consumer-side `getItemIcon` /
 * `getItemSubtitle` overrides.
 */
export interface SearchableItem {
  /** Stable id used as React key + passed to onItemSelect. */
  id: string;
  /** Primary label shown in the suggestion row. */
  name: string;
  /** Secondary line, used by the default subtitle renderer. */
  description?: string;
  /** Drives the default icon + boosts type-token scoring. */
  type?: string;
  /** Boosts tag-token scoring. */
  tags?: string[];
  /** Free-form passthrough for consumer-side rendering. */
  [key: string]: unknown;
}

export interface SmartSearchAssistantSlot {
  /** Footer label. Default: `"Ask Atlas"`. Atlas voice (no em-dashes). */
  label?: string;
  /** Fired when the footer row is clicked or activated via Enter. */
  onAsk: (query: string) => void;
}

export interface SmartSearchVoiceCallbacks {
  onTranscript?: (text: string) => void;
  onStart?: () => void;
  onStop?: () => void;
}

/**
 * Pre-grouped suggestions. When passed via `groups`, SmartSearch renders a
 * section header per group and concatenates items into a single keyboard-nav
 * list. The built-in scorer is bypassed — items are rendered in the order the
 * consumer provides (the consumer typically pre-fetched + pre-filtered them).
 */
export interface SmartSearchGroup {
  /** Stable key for React. */
  key: string;
  /** Header label rendered above the group's rows. */
  label: string;
  /** Optional icon before the label. */
  icon?: React.ReactNode;
  /** Items in this group. Rendered as-is, no scoring. */
  items: SearchableItem[];
}

/**
 * Secondary footer row that navigates to a full search results page. Sits
 * below `assistant` (Ask Aiko) so both can coexist as bottom links.
 */
export interface SmartSearchAllSlot {
  /** Footer label. Default: `"Search all results"`. */
  label?: string;
  /** Fired when the row is clicked or activated via Enter. */
  onSearch: (query: string) => void;
}

export interface SmartSearchProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "size" | "type" | "value" | "onChange"
> {
  /** Controlled value. */
  value: string;
  /** Controlled change handler. Fires for keystrokes, voice transcript, and clear. */
  onChange: (next: string) => void;

  /** Items to score + render in the dropdown. Ignored when `groups` is provided. */
  data?: SearchableItem[];
  /** Pre-grouped items. When provided, takes precedence over `data` and bypasses the built-in scorer. */
  groups?: SmartSearchGroup[];
  /** Click handler for a suggestion row. */
  onItemSelect?: (item: SearchableItem) => void;
  /** Override the default item icon (type-driven Lucide icon). */
  getItemIcon?: (item: SearchableItem) => React.ReactNode;
  /** Override the default subtitle (joins `type` + `status` with " · "). */
  getItemSubtitle?: (item: SearchableItem) => string;
  /** Cap on visible suggestions. Default 5. Only applies when `data` is used; `groups` are rendered as-passed. */
  maxResults?: number;

  /** Cycle through these as the placeholder while value is empty + input is unfocused. Reduce-motion users see only the first prompt. */
  placeholderPrompts?: string[];
  /** Static placeholder. Wins over `placeholderPrompts` when both are passed. */
  placeholder?: string;

  /** Enable voice input. `true` shows the mic when the browser supports the Web Speech API. Pass an object to listen to lifecycle events. */
  voice?: boolean | SmartSearchVoiceCallbacks;

  /** Render an "Ask <assistant> ..." footer in the dropdown. */
  assistant?: SmartSearchAssistantSlot;
  /** Render a "Search all results" footer in the dropdown. Coexists with `assistant`. */
  searchAll?: SmartSearchAllSlot;

  /** Async results are in-flight. Shows a spinner in the trailing chrome and skeleton rows in the dropdown when no results are visible yet. */
  loading?: boolean;
  /** Empty-state copy when query length is non-trivial and there are no results. Default: `'No results for "<query>"'`. Pass a function for a custom node. */
  emptyMessage?: string | ((query: string) => React.ReactNode);

  /** Override the leading icon. Defaults to the Search lucide. Pass `<AikoMark size="xs" />` for the brand-prefix variant. */
  leadingIcon?: React.ReactNode;

  /** Wrapper className for the outer positioned container. */
  wrapperClassName?: string;
}

/* ============================================================================
 * Internal helpers
 * ========================================================================= */

const TYPE_SPEED_MS = 50;
const HOLD_MS = 2000;
const ERASE_SPEED_MS = 30;
const VOICE_SILENCE_TIMEOUT_MS = 2000;
const QUERY_TRUNCATE = 30;

/** Score the data array against the query and slice to the top `limit`. */
function scoreItems(query: string, data: SearchableItem[], limit: number): SearchableItem[] {
  const q = query.trim().toLowerCase();
  if (!q || data.length === 0) return [];

  const scored: Array<{ item: SearchableItem; score: number }> = [];
  for (const item of data) {
    const name = (item.name ?? "").toLowerCase();
    const description = (item.description ?? "").toLowerCase();
    const type = (item.type ?? "").toLowerCase();
    const tags = (item.tags ?? []).map((t) => t.toLowerCase());

    let score = 0;
    if (name === q) score += 100;
    else if (name.startsWith(q)) score += 50;
    else if (name.includes(q)) score += 30;

    if (tags.some((t) => t.includes(q))) score += 20;
    if (type.includes(q)) score += 15;
    if (description.includes(q)) score += 10;

    if (score > 0) scored.push({ item, score });
  }

  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((s) => s.item);
}

/**
 * Cycles `prompts` through type → hold → erase phases. Returns the current
 * placeholder string. Pauses (returns frozen value) when `paused` is true.
 * Reduce-motion users get `prompts[0]` and never animate.
 */
function useTypewriterPlaceholder(
  prompts: string[] | undefined,
  paused: boolean,
  reduced: boolean | null,
): string {
  const [text, setText] = React.useState(prompts?.[0] ?? "");
  const [phraseIdx, setPhraseIdx] = React.useState(0);
  const [phase, setPhase] = React.useState<"typing" | "holding" | "erasing">("typing");

  React.useEffect(() => {
    if (!prompts || prompts.length === 0) return;
    if (reduced) {
      setText(prompts[0] ?? "");
      return;
    }
    if (paused) return;

    const phrase = prompts[phraseIdx % prompts.length] ?? "";
    let timer: ReturnType<typeof setTimeout> | undefined;

    if (phase === "typing") {
      if (text.length < phrase.length) {
        timer = setTimeout(() => setText(phrase.slice(0, text.length + 1)), TYPE_SPEED_MS);
      } else {
        timer = setTimeout(() => setPhase("holding"), HOLD_MS);
      }
    } else if (phase === "holding") {
      timer = setTimeout(() => setPhase("erasing"), HOLD_MS);
    } else if (phase === "erasing") {
      if (text.length > 0) {
        timer = setTimeout(() => setText(text.slice(0, -1)), ERASE_SPEED_MS);
      } else {
        setPhraseIdx((i) => (i + 1) % prompts.length);
        setPhase("typing");
      }
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [text, phase, phraseIdx, prompts, paused, reduced]);

  return reduced ? (prompts?.[0] ?? "") : text;
}

interface VoiceState {
  isListening: boolean;
  supported: boolean | null;
  start: () => void;
  stop: () => void;
}

/**
 * Web Speech API wrapper, SSR-safe. `supported` is null until the first
 * client-side resolution so SSR markup doesn't render the mic, then flash it
 * on hydration.
 */
function useVoiceInputInternal(
  enabled: boolean,
  onTranscript: (text: string) => void,
  callbacks?: SmartSearchVoiceCallbacks,
): VoiceState {
  const [supported, setSupported] = React.useState<boolean | null>(null);
  const [isListening, setIsListening] = React.useState(false);
  const recognitionRef = React.useRef<unknown>(null);
  const silenceTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const onTranscriptRef = React.useRef(onTranscript);
  const callbacksRef = React.useRef(callbacks);

  React.useEffect(() => {
    onTranscriptRef.current = onTranscript;
    callbacksRef.current = callbacks;
  }, [onTranscript, callbacks]);

  React.useEffect(() => {
    if (!enabled) {
      setSupported(false);
      return;
    }
    if (typeof window === "undefined") return;

    type SpeechRecognitionCtor = new () => {
      continuous: boolean;
      interimResults: boolean;
      lang: string;
      onstart: (() => void) | null;
      onresult:
        | ((event: {
            resultIndex: number;
            results: { isFinal: boolean; 0: { transcript: string } }[];
          }) => void)
        | null;
      onerror: ((event: { error: string }) => void) | null;
      onend: (() => void) | null;
      start: () => void;
      stop: () => void;
    };
    const win = window as unknown as {
      SpeechRecognition?: SpeechRecognitionCtor;
      webkitSpeechRecognition?: SpeechRecognitionCtor;
    };
    const Ctor = win.SpeechRecognition ?? win.webkitSpeechRecognition;
    if (!Ctor) {
      setSupported(false);
      return;
    }

    const recognition = new Ctor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    recognition.onstart = () => {
      setIsListening(true);
      callbacksRef.current?.onStart?.();
    };

    recognition.onresult = (event) => {
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
      let final = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const r = event.results[i];
        if (r?.isFinal) final += `${r[0].transcript} `;
      }
      // Web Speech fires a result event on every interim update. Earlier
      // versions forwarded `final || interim` straight through to the
      // consumer, which made each speech tick re-fire the search input
      // (mic + result list flash, the consumer's onTranscript replaces
      // its query value on each call). Commit only finals — same fix the
      // shared `useVoiceInput` hook landed in 0.10.8.
      if (final) {
        onTranscriptRef.current(final);
        callbacksRef.current?.onTranscript?.(final);
      }
      silenceTimerRef.current = setTimeout(() => {
        try {
          recognition.stop();
        } catch {
          /* ignore: stop on already-stopped instance throws on some browsers */
        }
      }, VOICE_SILENCE_TIMEOUT_MS);
    };

    recognition.onerror = () => {
      setIsListening(false);
      callbacksRef.current?.onStop?.();
    };

    recognition.onend = () => {
      setIsListening(false);
      callbacksRef.current?.onStop?.();
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }
    };

    recognitionRef.current = recognition;
    setSupported(true);

    return () => {
      try {
        recognition.stop();
      } catch {
        /* ignore */
      }
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    };
  }, [enabled]);

  const start = React.useCallback(() => {
    const r = recognitionRef.current as { start: () => void } | null;
    if (!r || isListening) return;
    try {
      r.start();
    } catch {
      /* ignore — chromium throws if start is called while already started */
    }
  }, [isListening]);

  const stop = React.useCallback(() => {
    const r = recognitionRef.current as { stop: () => void } | null;
    if (!r || !isListening) return;
    try {
      r.stop();
    } catch {
      /* ignore */
    }
  }, [isListening]);

  return { isListening, supported, start, stop };
}

/* ============================================================================
 * Default renderers
 * ========================================================================= */

const defaultGetItemIcon = (item: SearchableItem): React.ReactNode => {
  const type = (item.type ?? "").toLowerCase();
  const className = "h-4 w-4 text-muted-foreground";
  if (type.includes("pdf") || type.includes("doc")) return <FileText className={className} />;
  if (type.includes("folder") || type.includes("collection"))
    return <FolderOpen className={className} />;
  if (type.includes("quest") || type.includes("note")) return <BookOpen className={className} />;
  return <Lightbulb className={className} />;
};

const defaultGetItemSubtitle = (item: SearchableItem): string => {
  const parts: string[] = [];
  if (item.type) parts.push(item.type);
  const status = (item as { status?: unknown }).status;
  if (typeof status === "string" && status) parts.push(status);
  return parts.join(" · ");
};

const truncateQuery = (q: string): string =>
  q.length > QUERY_TRUNCATE ? `${q.slice(0, QUERY_TRUNCATE)}...` : q;

/* ============================================================================
 * Component
 * ========================================================================= */

let listboxIdCounter = 0;
const nextListboxId = () => `smartsearch-listbox-${++listboxIdCounter}`;

/**
 * SmartSearch — AI-aware search input with voice, animated placeholder,
 * scored fuzzy match, and an optional "Ask Atlas" footer.
 *
 * Sibling to `<ExploreSearchBar>` (lean default). SmartSearch is the variant
 * for surfaces that want the assistant-flavored affordances; consumers who
 * just need a vanilla search input should keep using `<ExploreSearchBar>`.
 *
 * Composes pretty primitives — `<ActionInput variant="search" size="lg">`
 * for the chrome, `<Button variant="ghost">` for clear + mic icons,
 * `<AikoMark size="xs">` for the AI footer mascot. No new external deps —
 * voice input is the native Web Speech API, feature-detected and SSR-safe.
 *
 * @example
 * ```tsx
 * <SmartSearch
 *   value={query}
 *   onChange={setQuery}
 *   data={items}
 *   onItemSelect={(item) => navigate(item.id)}
 *   placeholderPrompts={[
 *     "Search your content...",
 *     "Find a quest...",
 *     "Search by tag or status...",
 *   ]}
 *   voice
 *   assistant={{ label: "Ask Atlas", onAsk: openAskPanel }}
 * />
 * ```
 *
 * @example AISquare-mascot leading icon
 * ```tsx
 * <SmartSearch
 *   leadingIcon={<AikoMark size="xs" />}
 *   value={query}
 *   onChange={setQuery}
 *   data={items}
 * />
 * ```
 *
 * @example Server-fed sections + loading + dual footer
 * ```tsx
 * <SmartSearch
 *   value={query}
 *   onChange={setQuery}
 *   loading={isFetching}
 *   groups={[
 *     { key: "studios", label: "AI Studios", icon: <Building2 className="h-3 w-3" />, items: studios },
 *     { key: "experiences", label: "Experiences", icon: <Sparkles className="h-3 w-3" />, items: experiences },
 *   ]}
 *   assistant={{ label: "Ask AIKO", onAsk: askAiko }}
 *   searchAll={{ onSearch: (q) => navigate(`/explore?q=${encodeURIComponent(q)}`) }}
 * />
 * ```
 *
 * Composition + design rules: see `aisquare-pretty/DESIGN.md` (Voice + Anatomy).
 */
export const SmartSearch = React.forwardRef<HTMLInputElement, SmartSearchProps>(
  (
    {
      value,
      onChange,
      data,
      groups,
      onItemSelect,
      getItemIcon,
      getItemSubtitle,
      maxResults = 5,
      placeholderPrompts,
      placeholder,
      voice,
      assistant,
      searchAll,
      loading = false,
      emptyMessage,
      leadingIcon,
      wrapperClassName,
      className,
      onFocus,
      onBlur,
      onKeyDown,
      ...rest
    },
    ref,
  ) => {
    const reduced = useReducedMotion();
    const containerRef = React.useRef<HTMLDivElement>(null);
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const [focused, setFocused] = React.useState(false);
    const [open, setOpen] = React.useState(false);
    const [highlight, setHighlight] = React.useState(0);
    const listboxIdRef = React.useRef<string>("");
    if (!listboxIdRef.current) listboxIdRef.current = nextListboxId();

    const voiceEnabled = voice === true || (typeof voice === "object" && voice !== null);
    const voiceCallbacks = typeof voice === "object" && voice !== null ? voice : undefined;

    const {
      isListening,
      supported: voiceSupported,
      start: voiceStart,
      stop: voiceStop,
    } = useVoiceInputInternal(voiceEnabled, onChange, voiceCallbacks);

    const cycledPlaceholder = useTypewriterPlaceholder(
      placeholderPrompts,
      Boolean(placeholder) || focused || value.length > 0,
      reduced,
    );

    // Groups (consumer-curated, no re-scoring) take precedence over data
    // (flat, scored client-side). The flat `items` array drives keyboard nav
    // regardless — every group's items are concatenated in render order.
    const groupedItems = React.useMemo(
      () => (groups ? groups.flatMap((g) => g.items) : null),
      [groups],
    );
    const scoredItems = React.useMemo(
      () => scoreItems(value, data ?? [], maxResults),
      [value, data, maxResults],
    );
    const items: SearchableItem[] = groupedItems ?? scoredItems;

    // Map flat-index → group, keyed at the index of the FIRST item in that
    // group. Render-time we render the section header just before that item.
    // Empty groups are skipped so we don't show a header with no rows.
    const groupBoundaries = React.useMemo(() => {
      if (!groups) return null;
      const map = new Map<number, SmartSearchGroup>();
      let acc = 0;
      for (const g of groups) {
        if (g.items.length > 0) map.set(acc, g);
        acc += g.items.length;
      }
      return map;
    }, [groups]);

    const trimmedValue = value.trim();
    const showAskFooter = Boolean(assistant) && trimmedValue.length > 0;
    const showSearchAllFooter = Boolean(searchAll) && trimmedValue.length > 0;
    const showEmptyState = !loading && trimmedValue.length >= 2 && items.length === 0;
    const showSkeletonRows = loading && items.length === 0;

    // Footers + non-interactive informational rows are kept out of `totalRows`
    // for keyboard nav; only items + footers are selectable.
    const footerCount = (showAskFooter ? 1 : 0) + (showSearchAllFooter ? 1 : 0);
    const totalRows = items.length + footerCount;
    const askFooterIndex = showAskFooter ? items.length : -1;
    const searchAllFooterIndex = showSearchAllFooter ? items.length + (showAskFooter ? 1 : 0) : -1;

    const hasDropdownContent = totalRows > 0 || showSkeletonRows || showEmptyState;
    const dropdownVisible = open && hasDropdownContent;

    React.useEffect(() => {
      setHighlight((h) => Math.min(Math.max(h, 0), Math.max(totalRows - 1, 0)));
    }, [totalRows]);

    React.useEffect(() => {
      if (typeof document === "undefined") return;
      const handler = (e: MouseEvent) => {
        if (!containerRef.current) return;
        if (!containerRef.current.contains(e.target as Node)) {
          setOpen(false);
        }
      };
      document.addEventListener("mousedown", handler);
      return () => document.removeEventListener("mousedown", handler);
    }, []);

    const resolvedPlaceholder =
      placeholder ??
      (placeholderPrompts && placeholderPrompts.length > 0 ? cycledPlaceholder : "Search");

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
      setOpen(true);
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(true);
      if (value.length > 0) setOpen(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      window.setTimeout(() => setFocused(false), 150);
      onBlur?.(e);
    };

    const closeAfterCommit = () => {
      setOpen(false);
      setHighlight(0);
    };

    const commitItem = (item: SearchableItem) => {
      onItemSelect?.(item);
      onChange(item.name);
      closeAfterCommit();
    };

    const commitAsk = () => {
      assistant?.onAsk(value);
      closeAfterCommit();
    };

    const commitSearchAll = () => {
      searchAll?.onSearch(value);
      closeAfterCommit();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      onKeyDown?.(e);
      if (e.defaultPrevented) return;
      if (!dropdownVisible) {
        if (e.key === "ArrowDown" && hasDropdownContent) {
          setOpen(true);
          e.preventDefault();
        }
        return;
      }
      // Skeleton + empty-state rows aren't selectable; treat totalRows of 0
      // as "nothing to navigate" and absorb the arrow without moving focus.
      if (e.key === "ArrowDown") {
        e.preventDefault();
        if (totalRows === 0) return;
        setHighlight((h) => (h + 1) % totalRows);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        if (totalRows === 0) return;
        setHighlight((h) => (h - 1 + totalRows) % totalRows);
      } else if (e.key === "Enter") {
        if (highlight < items.length) {
          const item = items[highlight];
          if (item) {
            e.preventDefault();
            commitItem(item);
          }
        } else if (highlight === askFooterIndex) {
          e.preventDefault();
          commitAsk();
        } else if (highlight === searchAllFooterIndex) {
          e.preventDefault();
          commitSearchAll();
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
      }
    };

    const handleClear = () => {
      onChange("");
      setHighlight(0);
      inputRef.current?.focus();
    };

    const handleVoiceClick = () => {
      if (!voiceSupported) return;
      if (isListening) voiceStop();
      else voiceStart();
    };

    // Both buttons stay mounted regardless of state — toggling visibility via
    // opacity + pointer-events keeps the trailing slot's bounding box stable
    // so ActionInput's <motion.div layout> doesn't fire a layout animation
    // every time `value` flips between empty and non-empty (which would shift
    // and re-measure the mic on every keystroke — the "flashing" symptom).
    const clearVisible = value.length > 0;
    const micVisible = voiceEnabled && voiceSupported === true;
    const loadingVisible = loading && trimmedValue.length > 0;
    const trailingActions = (
      <div className="flex items-center gap-0.5">
        {/* Inline spinner — surfaces query-wiring latency in the chrome so users
            don't get dead air during the debounce + fetch window. Mounted always
            (opacity toggle) so it doesn't perturb ActionInput's layout. */}
        <span
          aria-hidden={!loadingVisible}
          className={cn(
            "inline-flex h-8 w-8 items-center justify-center text-muted-foreground",
            "transition-opacity duration-150",
            !loadingVisible && "opacity-0 pointer-events-none",
          )}
          role={loadingVisible ? "status" : undefined}
          aria-label={loadingVisible ? "Searching" : undefined}
        >
          <Spinner size="sm" decorative />
        </span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          motion={false}
          aria-label="Clear search"
          aria-hidden={!clearVisible}
          tabIndex={clearVisible ? 0 : -1}
          onClick={handleClear}
          className={cn(
            "h-8 w-8 rounded-full text-muted-foreground hover:text-foreground",
            "transition-opacity duration-150",
            !clearVisible && "opacity-0 pointer-events-none",
          )}
        >
          <X className="h-4 w-4" />
        </Button>
        {voiceEnabled ? (
          <MicButton
            listening={isListening}
            onClick={handleVoiceClick}
            aria-hidden={!micVisible}
            tabIndex={micVisible ? 0 : -1}
            // SSR feature-detect fades the mic in once the browser confirms
            // SpeechRecognition support. Opacity transitions on top of the
            // primitive's shared mic chrome.
            className={cn(
              "transition-opacity duration-150",
              !micVisible && "opacity-0 pointer-events-none",
            )}
          />
        ) : null}
      </div>
    );

    const activeDescendant = dropdownVisible
      ? highlight < items.length
        ? `${listboxIdRef.current}-item-${items[highlight]?.id ?? highlight}`
        : highlight === askFooterIndex
          ? `${listboxIdRef.current}-ask`
          : highlight === searchAllFooterIndex
            ? `${listboxIdRef.current}-search-all`
            : undefined
      : undefined;

    return (
      <div ref={containerRef} className={cn("relative w-full", wrapperClassName)}>
        <ActionInput
          ref={inputRef as React.Ref<HTMLInputElement | HTMLTextAreaElement>}
          variant="search"
          size="lg"
          value={value}
          placeholder={resolvedPlaceholder}
          leadingIcon={leadingIcon ?? <Search className="h-4 w-4" />}
          trailingAction={trailingActions}
          showActionWhen="always"
          onChange={
            handleChange as unknown as React.ChangeEventHandler<
              HTMLInputElement | HTMLTextAreaElement
            >
          }
          onFocus={
            handleFocus as unknown as React.FocusEventHandler<
              HTMLInputElement | HTMLTextAreaElement
            >
          }
          onBlur={
            handleBlur as unknown as React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
          }
          onKeyDown={
            handleKeyDown as unknown as React.KeyboardEventHandler<
              HTMLInputElement | HTMLTextAreaElement
            >
          }
          role="combobox"
          aria-expanded={dropdownVisible}
          aria-controls={listboxIdRef.current}
          aria-autocomplete="list"
          aria-activedescendant={activeDescendant}
          className={className}
          {...rest}
        />

        <AnimatePresence>
          {dropdownVisible ? (
            <motion.div
              key="smartsearch-dropdown"
              id={listboxIdRef.current}
              role="listbox"
              initial={reduced ? false : { opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduced ? { opacity: 0 } : { opacity: 0, y: -4 }}
              transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
              className={cn(
                "absolute top-full left-0 right-0 mt-2 z-50",
                "rounded-2xl border border-input bg-popover text-popover-foreground shadow-lg overflow-hidden",
              )}
            >
              {showSkeletonRows ? (
                <div className="p-3 space-y-2.5" aria-hidden="true">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="flex items-center gap-3 px-1">
                      <Skeleton variant="avatar" className="h-8 w-8" />
                      <div className="flex-1 space-y-1.5">
                        <Skeleton variant="text" className="h-3 w-3/4" />
                        <Skeleton variant="text" className="h-2.5 w-1/2" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  {items.map((item, idx) => {
                    const optionId = `${listboxIdRef.current}-item-${item.id}`;
                    const selected = idx === highlight;
                    const groupHeader = groupBoundaries?.get(idx);
                    return (
                      <React.Fragment key={item.id}>
                        {groupHeader ? (
                          <div
                            className={cn(
                              "flex items-center gap-2 px-4 pt-3 pb-1",
                              "text-[11px] font-semibold uppercase tracking-wide text-muted-foreground",
                              idx > 0 && "border-t border-border/40 mt-1",
                            )}
                          >
                            {groupHeader.icon ? (
                              <span className="text-muted-foreground" aria-hidden="true">
                                {groupHeader.icon}
                              </span>
                            ) : null}
                            <span>{groupHeader.label}</span>
                            <span className="font-normal normal-case tracking-normal text-muted-foreground/60">
                              ({groupHeader.items.length})
                            </span>
                          </div>
                        ) : null}
                        <motion.button
                          id={optionId}
                          type="button"
                          role="option"
                          aria-selected={selected}
                          onMouseDown={(e) => {
                            // mouseDown beats blur; commit before the input closes
                            e.preventDefault();
                            commitItem(item);
                          }}
                          onMouseEnter={() => setHighlight(idx)}
                          // Stagger entry per index so freshly-arrived results
                          // ripple in instead of slamming as a block. New
                          // queries replace items array → React unmounts old
                          // rows + remounts new ones, which re-fires this.
                          // Reduce-motion users get instant render.
                          initial={reduced ? false : { opacity: 0, y: -4 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={
                            reduced
                              ? { duration: 0 }
                              : {
                                  duration: 0.22,
                                  delay: Math.min(idx * 0.025, 0.2),
                                  ease: [0.16, 1, 0.3, 1],
                                }
                          }
                          className={cn(
                            "w-full px-4 py-3 flex items-center gap-3 text-left transition-colors",
                            "focus-visible:outline-none",
                            selected ? "bg-accent/10 text-accent-foreground" : "hover:bg-muted/40",
                          )}
                        >
                          <span className="flex-shrink-0">
                            {(getItemIcon ?? defaultGetItemIcon)(item)}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm text-foreground truncate">
                              {item.name}
                            </div>
                            <div className="text-xs text-muted-foreground truncate">
                              {(getItemSubtitle ?? defaultGetItemSubtitle)(item)}
                            </div>
                          </div>
                        </motion.button>
                      </React.Fragment>
                    );
                  })}
                  {showEmptyState ? (
                    <div
                      className="px-4 py-6 text-center text-sm text-muted-foreground"
                      role="status"
                    >
                      {typeof emptyMessage === "function"
                        ? emptyMessage(trimmedValue)
                        : (emptyMessage ?? (
                            <>
                              No results for &ldquo;
                              <span className="text-foreground font-medium">
                                {truncateQuery(trimmedValue)}
                              </span>
                              &rdquo;
                            </>
                          ))}
                    </div>
                  ) : null}
                </>
              )}
              {showAskFooter && assistant ? (
                <button
                  id={`${listboxIdRef.current}-ask`}
                  type="button"
                  role="option"
                  aria-selected={highlight === askFooterIndex}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    commitAsk();
                  }}
                  onMouseEnter={() => setHighlight(askFooterIndex)}
                  className={cn(
                    "w-full px-4 py-3 flex items-center gap-2 text-left transition-colors",
                    "border-t border-border bg-gradient-to-r from-primary/5 to-transparent",
                    "focus-visible:outline-none",
                    highlight === askFooterIndex ? "bg-accent/10" : "hover:bg-muted/30",
                  )}
                >
                  <AikoMark size="xs" />
                  <span className="text-sm text-muted-foreground">
                    {assistant.label ?? "Ask Atlas"} &ldquo;
                    <span className="text-primary font-medium">{truncateQuery(value)}</span>
                    &rdquo;
                  </span>
                </button>
              ) : null}
              {showSearchAllFooter && searchAll ? (
                <button
                  id={`${listboxIdRef.current}-search-all`}
                  type="button"
                  role="option"
                  aria-selected={highlight === searchAllFooterIndex}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    commitSearchAll();
                  }}
                  onMouseEnter={() => setHighlight(searchAllFooterIndex)}
                  className={cn(
                    "w-full px-4 py-3 flex items-center gap-2 text-left transition-colors",
                    "border-t border-border",
                    "focus-visible:outline-none",
                    highlight === searchAllFooterIndex ? "bg-accent/10" : "hover:bg-muted/30",
                  )}
                >
                  <Search className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                  <span className="text-sm text-muted-foreground">
                    {searchAll.label ?? "Search all results"} &ldquo;
                    <span className="text-primary font-medium">{truncateQuery(value)}</span>
                    &rdquo;
                  </span>
                </button>
              ) : null}
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    );
  },
);
SmartSearch.displayName = "SmartSearch";
